import { inject, Injectable, signal, WritableSignal } from "@angular/core";
import { ActivatedRouteSnapshot, CanDeactivateFn, Params, ResolveFn, Router, RouterStateSnapshot } from "@angular/router";
import { FlushDTypeDataItem, FlushDTypeDataItemReply, JsonReply, SelectOption } from "@grupo-constant/next-dtype/types";
import { TranslocoService } from "@ngneat/transloco";
import { eachDayOfInterval, endOfMonth, format, setMonth, setYear, startOfMonth } from "date-fns";
import { catchError, map, Observable, of, throwError } from "rxjs";
import { CalculateNet4ConceptsRequest, ChangeWorkplaceGuideRequest, DeleteNet4ConceptMappingRequest, DownloadContractWorkSummaryXSSFRequest, DownloadNet4WorkSummaryXSSFRequest, DownloadTrabajadorNet4WorkSummaryXSSFRequest, DownloadWorkplaceWorkSummaryXSSFRequest, FlushCalendarRequest, FlushDailyWorkplaceWorkConceptSummaryRequest, FlushMonthlyContractWorkConceptSummaryRequest, FlushMonthlyWorkplaceWorkConceptSummaryRequest, FlushNet4ConceptMappingRequest, FlushNet4WorkerGroupRequest, FlushUserNodePriviligesRequest, HackContractMinutesRequest, HackContractMinutesV2Request, RegularizeHourBagRequest, RetrieveCalendarDateListRequest, RetrieveCalendarWithDNRequest, RetrieveContractWorkSummaryRequest, RetrieveDailyWorkplaceWorkConceptSummaryAggregateRequest, RetrieveDailyWorkplaceWorkConceptSummaryRequest, RetrieveDTypeDataCollectionRequest, RetrieveDTypeDataItemRequest, RetrieveErrorWorkConceptSummaryRequest, RetrieveMonthlyContractWorkConceptSummaryRequest, RetrieveMonthlyWorkplaceWorkConceptSummaryRequest, RetrieveNet4ConceptMappingRequest, RetrieveNet4WorkerAssignmentsRequest, RetrieveNet4WorkerGroupRequest, RetrieveNet4WorkSummaryRequest, RetrieveProductividadPresenciaRequest, RetrieveTrabajadorNet4WorkSummaryRequest, RetrieveWorkplaceWorkSummaryRequest, SearchBusPartakerRequest, SearchDBNet4WorkerRequest, SearchNet4PresenceConceptRequest, SynchronizeBeepleWorkplaceRequest, SynchronizeBeepleWorkplaceWeekRequest, ToggleHourBagRequest, ValidateDailyWorkplaceWorkConceptSummaryRequest, ValidateMonhtlyContractWorkConceptSummaryRequest, ValidateMonhtlyWorkplaceWorkConceptSummaryRequest, ValidateWorkplaceWorkConceptSummaryRequest } from '../../../core/http/request.model';
import { OUService } from "../ou/ou.service";
import { OU_URI } from "../ou/ou.types";
import { adminUri, backofficeUri, BeepleClient, ConceptSummaryColumn, ConceptSummaryRow, HttpServiceEndpoint, HttpServiceEndpointUri, MasterEndpointTargetSystem, MasterEndpointTargetSystemUri, MonthlyContractWorkConceptSummary, NextServicio, Partaker, UserNodePrivileges, UserProfile } from "./endpoints.types";
import { FuseConfirmationService } from "../../../../@fuse/services/confirmation/confirmation.service";
import { DialogsService } from "../../../dialogs/dialogs.service";
import { HttpService } from "../../../core/http/http.service";
import { LocalizeService } from "../../../core/pipes/localize.service";
import { MasterService } from "../../../core/master/master.service";
import { ChangeWorkplaceGuideReply, FlushCalendarReply, FlushMonthlyContractWorkConceptSummaryReply, FlushNet4ConceptMappingReply, FlushNet4WorkerGroupReply, FlushUserNodePriviligesReply, FlushWorkplaceWorkConceptSummaryReply, HackContractMinutesReply, RegularizeHourBagReply, RegularizeHourBagWithConceptsReply, RetrieveCalendarDateListReply, RetrieveCalendarWithDNReply, RetrieveContractWorkSummaryReply, RetrieveDTypeDataItemReply, RetrieveErrorWorkConceptSummaryReply, RetrieveMonthlyContractWorkConceptSummaryReply, RetrieveMonthlyContractWorkConceptSummaryTransformedReply, RetrieveNet4ConceptMappingReply, RetrieveNet4WorkerAssignmentsReply, RetrieveNet4WorkerGroupReply, RetrieveProductividadPresenciaReply, RetrieveWorkplaceWorkConceptSummaryReply, RetrieveWorkplaceWorkConceptSummaryTransformedReply, RetrieveWorkSummaryReply, SearchDBNet4WorkerReply, SearchNet4AbsenceConceptReply, SynchronizeNextServicio_n2bReply, ToggleHourBagReply, ValidateContractWorkConceptSummaryReply, ValidateWorkplaceWorkConceptSummaryReply } from "../../../core/http/reply.model";
import { Net2BeepleMethodsEnum } from "../../../core/enums/net2beeple-methods.enum";
import { USER_URI } from "../../../core/user/user.types";
import { Net2BeepleLocator } from "../../../core/dtype/dtype.types";

export const endpointResolver: ResolveFn<any> = (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
    const params = route.queryParams;
    if (params?.ts_endpoint) {
        return inject(EndpointsService).retrieveMasterEndpointTargetSystemByOid(params.ts_endpoint);
    } else {
        return of({});
    }
}

export const httpEndpointResolver: ResolveFn<any> = (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
    const params = route.queryParams;
    if (params?.http_endpoint) {
        return inject(EndpointsService).retrieveHttpServiceEndpointByOid(params.http_endpoint);
    } else {
        return of({});
    }
}

export const beepleClientResolver: ResolveFn<any> = (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
    const params = route.queryParams;
    if (params.client && params.http_endpoint && params.ts_endpoint) {
        return inject(EndpointsService).retrieveBeepleClientByOid(params.client, params.http_endpoint, params.ts_endpoint);
    } else {
        return of({});
    }
}

export const nextServiceResolver: ResolveFn<any> = (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
    const params = route.queryParams;
    if (params.service && params.http_endpoint && params.ts_endpoint) {
        return inject(EndpointsService).retrieveNextServiceByOid(params.service, params.http_endpoint, params.ts_endpoint);
    } else {
        return of({});
    }
}

export const canDeactivate: CanDeactivateFn<any> = (component: any, currentRoute: ActivatedRouteSnapshot, currentState: RouterStateSnapshot, nextState?: RouterStateSnapshot) => {
    // Si se intenta salir porque se ha refrescado en la pagina de edición se deja salir
    if (nextState.root.queryParams.force && nextState.root.queryParams.force !== 'false') {
        return true;
    }

    if (component.hasChanges === false) {
        return true;
    }

    const translocoService = inject(TranslocoService);
    const dialogRef = inject(FuseConfirmationService).open({
        title: translocoService.translate('unsavedChanges'),
        message: translocoService.translate('exitWithUnsavedChanges'),
        actions: {
            confirm: {
                label: translocoService.translate('exitWithoutSaving')
            },
            cancel: {
                label: translocoService.translate('cancel')
            }
        }
    });

    return new Promise<boolean>((resolve) => {
        dialogRef.afterClosed().subscribe((result) => {
            if (result === 'confirmed') {
                resolve(true);
            } else {
                resolve(false);
            }
        });
    });
}

@Injectable({
    providedIn: 'root'
})
export class EndpointsService {

    private dialogsService = inject(DialogsService);
    private httpService = inject(HttpService);
    private localize = inject(LocalizeService);
    private masterService = inject(MasterService);
    private ouService = inject(OUService);
    private router = inject(Router);
    private transloco = inject(TranslocoService);

    private _endpoint: WritableSignal<MasterEndpointTargetSystem> = signal(undefined);
    readonly endpoint = this._endpoint.asReadonly();
    private _httpEndpoint: WritableSignal<HttpServiceEndpoint> = signal(undefined);
    readonly httpEndpoint = this._httpEndpoint.asReadonly();
    private _beepleClient: WritableSignal<BeepleClient> = signal(undefined);
    readonly beepleClient = this._beepleClient.asReadonly();
    private _nextService: WritableSignal<NextServicio> = signal(undefined);
    readonly nextService = this._nextService.asReadonly();

    retrieveMasterEndpointTargetSystem(): Observable<MasterEndpointTargetSystem[]> {
        const request: RetrieveDTypeDataCollectionRequest = {
            columnsForList: false,
            count: false,
            uri: MasterEndpointTargetSystemUri,
            discardDeprecated: false,
        }
        return this.masterService.retrieveMasterDataDataCollection(request)
            .pipe(map(reply => reply.data ?? []));
    }

    retrieveMasterEndpointTargetSystemByOid(oid: string): Observable<MasterEndpointTargetSystem> {
        const request: RetrieveDTypeDataItemRequest = {
            uri: MasterEndpointTargetSystemUri,
            oid
        }
        return this.httpService.post<RetrieveDTypeDataItemReply>(Net2BeepleMethodsEnum.retrieveDTypeDataItem, request)
            .pipe(
                catchError((error) => {
                    this.router.navigateByUrl('/404');
                    return throwError(() => error);
                }),
                map(reply => {
                    reply.data.admin = reply.data[adminUri]?.map(u => {
                        u = { ...u, ...reply.partaker[USER_URI]?.[u.userId] };
                        return u;
                    }) ?? [];
                    this._endpoint.set(reply.data);
                    return reply.data;
                })
            );
    }

    flushMasterEndpointTargetSystem(endpoint: MasterEndpointTargetSystem) {
        const request: FlushDTypeDataItem = {
            uri: MasterEndpointTargetSystemUri,
            data: endpoint
        }
        return this.httpService.post<FlushDTypeDataItemReply>(Net2BeepleMethodsEnum.flushDTypeDataItem, request).pipe(
            catchError((error) => {
                this.dialogsService.openErrorDialog({
                    message: error?.error?.echo && Object.keys(error.error.echo).length > 0 ? this.localize.transform(error.error.echo) : undefined
                });
                return throwError(() => error);
            })
        );
    }

    retrieveHttpServiceEndpoint() : Observable<HttpServiceEndpoint[]> {
        const request: RetrieveDTypeDataCollectionRequest = {
            columnsForList: false,
            count: false,
            uri: HttpServiceEndpointUri,
            discardDeprecated: false,
        };
        return this.masterService.retrieveMasterDataDataCollection(request)
            .pipe(map(reply => reply.data ?? []));
    }

    retrieveHttpServiceEndpointByOid(oid: string): Observable<HttpServiceEndpoint> {
        const request: RetrieveDTypeDataItemRequest = {
            uri: HttpServiceEndpointUri,
            oid
        }
        return this.httpService.post<RetrieveDTypeDataItemReply>(Net2BeepleMethodsEnum.retrieveDTypeDataItem, request)
            .pipe(
                catchError((error) => {
                    this.router.navigateByUrl('/404');
                    return throwError(() => error);
                }),
                map(reply => {
                    reply.data.admin = reply.data[adminUri]?.map(u => {
                        u = { ...u, ...reply.partaker[USER_URI]?.[u.userId] };
                        return u;
                    }) ?? [];

                    reply.data.backoffice = reply.data[backofficeUri]?.map(u => {
                        u = { ...u, ...reply.partaker[USER_URI]?.[u.userId] };
                        return u;
                    }) ?? [];

                    this._httpEndpoint.set(reply.data);
                    return reply.data;
                })
            );
    }

    flushHttpServiceEndpointSystem(httpendpoint: HttpServiceEndpoint) {
        const request: FlushDTypeDataItem = {
            uri: HttpServiceEndpointUri,
            data: httpendpoint
        }
        return this.httpService.post<FlushDTypeDataItemReply>(Net2BeepleMethodsEnum.flushDTypeDataItem, request).pipe(
            catchError((error) => {
                this.dialogsService.openErrorDialog({
                    message: error?.error?.echo && Object.keys(error.error.echo).length > 0 ? this.localize.transform(error.error.echo) : undefined
                });
                return throwError(() => error);
            })
        );
    }

    retrieveBeepleClient(httpEndpoint: HttpServiceEndpoint) : Observable<BeepleClient[]> {
        const uri = `master://HS=${httpEndpoint._oid}/TS=${httpEndpoint.endpointTargetSystem}/OU=01/BeepleClient`;
        const request: RetrieveDTypeDataCollectionRequest = {
            columnsForList: false,
            count: false,
            uri,
            discardDeprecated: false,
        };
        return this.masterService.retrieveMasterDataDataCollection(request)
            .pipe(map(reply => reply.data ?? []));
    }

    retrieveBeepleClientByOid(oid: string, httpEndpoint: string, endpointTargetSystem: string): Observable<BeepleClient> {
        const uri = `master://HS=${httpEndpoint}/TS=${endpointTargetSystem}/OU=01/BeepleClient`;
        const request: RetrieveDTypeDataItemRequest = {
            uri,
            oid
        }
        return this.httpService.post<RetrieveDTypeDataItemReply>(Net2BeepleMethodsEnum.retrieveDTypeDataItem, request)
            .pipe(
                catchError((error) => {
                    this.router.navigateByUrl('/404');
                    return throwError(() => error);
                }),
                map(reply => {
                    reply.data.admin = reply.data[adminUri]?.map(u => {
                        u = { ...u, ...reply.partaker[USER_URI]?.[u.userId] };
                        return u;
                    }) ?? [];
                    reply.data.backoffice = reply.data[backofficeUri]?.map(u => {
                        u = { ...u, ...reply.partaker[USER_URI]?.[u.userId] };
                        return u;
                    }) ?? [];
                    reply.data.targetSystem = reply.partaker[MasterEndpointTargetSystemUri]?.[reply.data.endpointTargetSystem];
                    reply.data.httpEndpoint = reply.partaker[HttpServiceEndpointUri]?.[reply.data.endpoint];
                    this._beepleClient.set(reply.data);
                    return reply.data;
                })
            );
    }

    flushBeepleClient(client: BeepleClient) {
        const uri = `master://HS=${client.endpoint}/TS=${client.endpointTargetSystem}/OU=01/BeepleClient`;
        const request: FlushDTypeDataItem = {
            uri,
            data: client
        }
        return this.httpService.post<FlushDTypeDataItemReply>(Net2BeepleMethodsEnum.flushDTypeDataItem, request).pipe(
            catchError((error) => {
                this.dialogsService.openErrorDialog({
                    message: error?.error?.echo && Object.keys(error.error.echo).length > 0 ? this.localize.transform(error.error.echo) : undefined
                });
                return throwError(() => error);
            })
        );
    }

    retrieveNextServicio(httpEndpoint: HttpServiceEndpoint) : Observable<NextServicio[]> {
        const uri = `master://HS=${httpEndpoint._oid}/TS=${httpEndpoint.endpointTargetSystem}/OU=01/NextServicio_n2b`;
        const request: RetrieveDTypeDataCollectionRequest = {
            columnsForList: false,
            count: false,
            uri,
            discardDeprecated: false,
        };
        return this.masterService.retrieveMasterDataDataCollection(request)
            .pipe(map(reply => {
                const services: NextServicio[] = reply.data ?? [];
                return services.map(service => {
                    const payload = service.nodeInfo ? JSON.parse(service.nodeInfo) : undefined;
                    if (payload) {
                        service.brand = {
                            code: payload.code,
                            name: payload.name,
                            logoURL: payload.logoURL
                        };
                        if (payload.node) {
                            service.client = {
                                code: payload.node.code,
                                name: payload.node.name,
                                logoURL: payload.node.logoURL
                            };
                            if (payload.node.node) {
                                service.project = {
                                    code: payload.node.node.code,
                                    name: payload.node.node.name,
                                    logoURL: payload.node.node.logoURL
                                };
                            }
                        }
                    }
                    return service;
                }).filter(s => s.brand !== undefined);
            }));
    }

    retrieveNextServiceByOid(oid: string, httpEndpoint: string, endpointTargetSystem: string): Observable<NextServicio> {
        const uri = `master://HS=${httpEndpoint}/TS=${endpointTargetSystem}/OU=01/NextServicio_n2b`;
        const request: RetrieveDTypeDataItemRequest = {
            uri,
            oid
        }
        return this.httpService.post<RetrieveDTypeDataItemReply>(Net2BeepleMethodsEnum.retrieveDTypeDataItem, request)
            .pipe(
                catchError((error) => {
                    this.router.navigateByUrl('/404');
                    return throwError(() => error);
                }),
                map(reply => {
                    reply.data.admin = reply.data[adminUri]?.map(u => {
                        u = { ...u, ...reply.partaker[USER_URI]?.[u.userId] };
                        return u;
                    }) ?? [];
                    reply.data.backoffice = reply.data[backofficeUri]?.map(u => {
                        u = { ...u, ...reply.partaker[USER_URI]?.[u.userId] };
                        return u;
                    }) ?? [];
                    reply.data.targetSystem = reply.partaker[MasterEndpointTargetSystemUri]?.[reply.data.endpointTargetSystem];
                    reply.data.httpEndpoint = reply.partaker[HttpServiceEndpointUri]?.[reply.data.endpoint];
                    this._nextService.set(reply.data);
                    return reply.data;
                })
            );
    }

    flushNextService(service: NextServicio) {
        const uri = `master://HS=${service.endpoint}/TS=${service.endpointTargetSystem}/OU=01/NextServicio_n2b`;
        const request: FlushDTypeDataItem = {
            uri,
            data: service
        }
        return this.httpService.post<FlushDTypeDataItemReply>(Net2BeepleMethodsEnum.flushDTypeDataItem, request).pipe(
            catchError((error) => {
                this.dialogsService.openErrorDialog({
                    message: error?.error?.echo && Object.keys(error.error.echo).length > 0 ? this.localize.transform(error.error.echo) : undefined
                });
                return throwError(() => error);
            })
        );
    }

    synchronizeBeepleWorkplace(request: SynchronizeBeepleWorkplaceRequest) {
        return this.httpService.post<JsonReply>(Net2BeepleMethodsEnum.synchronizeBeepleWorkplace, request);
    }

    synchronizeBeepleWorkplaceWeek(request: SynchronizeBeepleWorkplaceWeekRequest) {
        return this.httpService.post<JsonReply>(Net2BeepleMethodsEnum.synchronizeBeepleWorkplaceWeek, request);
    }

    flushUserNodePriviliges(request: FlushUserNodePriviligesRequest): Observable<UserProfile> {
        return this.httpService.post<FlushUserNodePriviligesReply>(Net2BeepleMethodsEnum.flushUserNodePrivileges, request).pipe(
            catchError((error) => {
                this.dialogsService.openErrorDialog({
                    message: Object.keys(error?.error?.echo).length > 0 ? this.localize.transform(error.error.echo) : undefined,
                });
                return throwError(() => error);
            }),
            map((response) => {
                if (response?.userNodePrivileges?.length > 0) {
                    const users = response.userNodePrivileges.map((u) => {
                        const _user: UserProfile = { ...response.partaker[USER_URI][u.userId], ...u };
                        if (_user.grantor && _user.grantor !== 'system') {
                            _user.grantorName = response.partaker[USER_URI][_user.grantor].displayName;
                        }
                        return _user;
                    });
                    return users[0];
                }
            }),
        );
    }

    // retrieveEmpresasAsSelectOption(locator: Net2BeepleLocator): Observable<SelectOption[]> {
    //     // return this.httpService.post<{ option: SelectOption[] }>(Net2BeepleMethodsEnum.retrieveEmpresasAsSelectOption, { locator });
    //     return of([{
    //         code: '7',
    //         value: {
    //             'es-ES': 'G.C.SERVICIOS EMPRESARIALES, SLU',
    //         },
    //         export: {},
    //         _deprecated: false
    //     }, {
    //         code: '1',
    //         value: {
    //             'es-ES': 'PERSONAL 7 E.T.T., S.A.',
    //         },
    //         export: {},
    //         _deprecated: false
    //     }]);
    // }

    retrieveCalendarWithDN(request: RetrieveCalendarWithDNRequest, stringLocator: string) {
        return this.httpService.post<RetrieveCalendarWithDNReply>(Net2BeepleMethodsEnum.retrieveCalendarWithDN, request)
            .pipe(map(reply => {
                const calendar = reply?.collection?.map(cal => {
                    cal.belongsToCaption = this.getBelongsToCaption(cal.belongsTo, request.locator, reply.partaker);
                    cal.sameLocator = this.isTheSameLocator(stringLocator, cal.belongsTo);
                    return cal;
                });
                return calendar ?? [];
            }));
    }

    retrieveCalendarDateList(request: RetrieveCalendarDateListRequest) {
        return this.httpService.post<RetrieveCalendarDateListReply>(Net2BeepleMethodsEnum.retrieveCalendarDateList, request)
            .pipe(map(reply => reply?.dates?.map(d => d.holiday) ?? []));
    }

    flushCalendar(request: FlushCalendarRequest) {
        return this.httpService.post<FlushCalendarReply>(Net2BeepleMethodsEnum.flushCalendar, request)
        .pipe(map(reply => {
            const calendar = reply?.calendar;
            if (calendar) {
                calendar.belongsToCaption = this.getBelongsToCaption(calendar.belongsTo, request.locator, reply.partaker);
                calendar.sameLocator = true;
            }
            return calendar;
        }));
    }

    deleteCalendar(calendarOid: string) {
        return this.httpService.post<JsonReply>(Net2BeepleMethodsEnum.deleteCalendar, { calendarOid });
    }

    retrieveNet4ConceptMapping(request: RetrieveNet4ConceptMappingRequest) {
        return this.httpService.post<RetrieveNet4ConceptMappingReply>(Net2BeepleMethodsEnum.retrieveNet4ConceptMapping, request);
    }

    flushNet4ConceptMapping(request: FlushNet4ConceptMappingRequest) {
        return this.httpService.post<FlushNet4ConceptMappingReply>(Net2BeepleMethodsEnum.flushNet4ConceptMapping, request);
    }

    searchNet4PresenceConcept(request: SearchNet4PresenceConceptRequest) {
        return this.httpService.post<SearchNet4AbsenceConceptReply>(Net2BeepleMethodsEnum.searchNet4PresenceConcept, request)
            .pipe(map(reply => reply.net4Concepts ?? []));
    }

    searchDBNet4Worker(request: SearchDBNet4WorkerRequest) {
        return this.httpService.post<SearchDBNet4WorkerReply>(Net2BeepleMethodsEnum.searchDBNet4Worker, request)
            .pipe(map(reply => reply.net4Workers ?? []));
    }

    deleteNet4ConceptMapping(request: DeleteNet4ConceptMappingRequest) {
        return this.httpService.post<JsonReply>(Net2BeepleMethodsEnum.deleteNet4ConceptMapping, request);
    }

    retrieveWorkplaceWorkSummary(request: RetrieveWorkplaceWorkSummaryRequest) {
        return this.httpService.post<RetrieveWorkSummaryReply>(Net2BeepleMethodsEnum.retrieveWorkplaceWorkSummary, request)
            .pipe(map(reply => this.fillNoWorkedDays(request, reply)));
    }

    retrieveMonthlyWorkplaceWorkConceptSummary(request: RetrieveMonthlyWorkplaceWorkConceptSummaryRequest) {
        return this.httpService.post<RetrieveWorkplaceWorkConceptSummaryReply>(Net2BeepleMethodsEnum.retrieveMonthlyWorkplaceWorkConceptSummary, request)
            .pipe(map(reply => {
                const rows: ConceptSummaryRow[] = reply.rows.map(row => {
                    const cols = new Map<number, ConceptSummaryColumn>();
                    for (const col of Object.entries(row.cols)) {
                        cols.set(col[1].net4ConceptId, { ...col[1], oid: col[0] });
                    }
                    return { ...row, cols };
                });
                const transformedReply: RetrieveWorkplaceWorkConceptSummaryTransformedReply = { ...reply, rows };
                return transformedReply;
            }));
    }

    retrieveDailyWorkplaceWorkConceptSummary(request: RetrieveDailyWorkplaceWorkConceptSummaryRequest) {
        return this.httpService.post<RetrieveWorkplaceWorkConceptSummaryReply>(Net2BeepleMethodsEnum.retrieveDailyWorkplaceWorkConceptSummary, request) //Usa el genérico con la interfaz del monthly
            .pipe(map(reply => {
                const rows: ConceptSummaryRow[] = reply.rows.map(row => {
                    const cols = new Map<number, ConceptSummaryColumn>();
                    for (const col of Object.entries(row.cols)) {
                        cols.set(col[1].net4ConceptId, { ...col[1], oid: col[0] });
                    }
                    return { ...row, cols };
                });
                const transformedReply: RetrieveWorkplaceWorkConceptSummaryTransformedReply = { ...reply, rows };
                return transformedReply;
            }));
    }

    retrieveDailyWorkplaceWorkConceptSummaryAggregate (request: RetrieveDailyWorkplaceWorkConceptSummaryAggregateRequest) {
        return this.httpService.post<RetrieveWorkplaceWorkConceptSummaryReply>(Net2BeepleMethodsEnum.retrieveDailyWorkplaceWorkConceptSummaryAggregate, request)
            .pipe(map(reply => {
                const rows: ConceptSummaryRow[] = reply.rows.map(row => {
                    const cols = new Map<number, ConceptSummaryColumn>();
                    for (const col of Object.entries(row.cols)) {
                        cols.set(col[1].net4ConceptId, { ...col[1], oid: col[0] });
                    }
                    return { ...row, cols };
                });
                const transformedReply: RetrieveWorkplaceWorkConceptSummaryTransformedReply = { ...reply, rows };
                return transformedReply;
            }));
    }

    retrieveErrorWorkConceptSummary(request: RetrieveErrorWorkConceptSummaryRequest) {
        return this.httpService.post<RetrieveErrorWorkConceptSummaryReply>(Net2BeepleMethodsEnum.retrieveErrorWorkConceptSummary, request);
    }

//TODO: revisar return, quizá hay que transformar el reply
    retrieveContractWorkSummary (request: RetrieveContractWorkSummaryRequest) {
        return this.httpService.post<RetrieveContractWorkSummaryReply>(Net2BeepleMethodsEnum.retrieveContractWorkSummary, request)
            .pipe(map(reply => this.fillNoWorkedDaysContract(request, reply)));
    }

    flushMonthlyWorkplaceWorkConceptSummary(request: FlushMonthlyWorkplaceWorkConceptSummaryRequest) {
        return this.httpService.post<FlushWorkplaceWorkConceptSummaryReply>(Net2BeepleMethodsEnum.flushMonthlyWorkplaceWorkConceptSummary, request);
    }

    flushDailyWorkplaceWorkConceptSummary(request: FlushDailyWorkplaceWorkConceptSummaryRequest) {
        return this.httpService.post<FlushWorkplaceWorkConceptSummaryReply>(Net2BeepleMethodsEnum.flushDailyWorkplaceWorkConceptSummary, request);
    }

    validateMonthlyWorkplaceWorkConceptSummary(request: ValidateMonhtlyWorkplaceWorkConceptSummaryRequest) {
        return this.httpService.post<ValidateWorkplaceWorkConceptSummaryReply>(Net2BeepleMethodsEnum.validateMonthlyWorkplaceWorkConceptSummary, request);
    }

    validateDailyWorkplaceWorkConceptSummary(request: ValidateDailyWorkplaceWorkConceptSummaryRequest) {
        return this.httpService.post<ValidateWorkplaceWorkConceptSummaryReply>(Net2BeepleMethodsEnum.validateDailyWorkplaceWorkConceptSummary, request);
    }

    validateWorkplaceWorkConceptSummary(request: ValidateWorkplaceWorkConceptSummaryRequest) {
        return this.httpService.post<ValidateWorkplaceWorkConceptSummaryReply>(Net2BeepleMethodsEnum.validateWorkplaceWorkConceptSummary, request);
    }

    retrieveProductividadPresencia(request: RetrieveProductividadPresenciaRequest) {
        return this.httpService.post<RetrieveProductividadPresenciaReply>(Net2BeepleMethodsEnum.retrieveProductividadPresencia, request);
    }

    calculateNet4Concepts(request: CalculateNet4ConceptsRequest) {
        return this.httpService.post(Net2BeepleMethodsEnum.calculateNet4Concepts, request);
    }

    retrieveNet4WorkerAssignments(request: RetrieveNet4WorkerAssignmentsRequest) {
        return this.httpService.post<RetrieveNet4WorkerAssignmentsReply>(Net2BeepleMethodsEnum.retrieveNet4WorkerAssignments, request)
            .pipe(map(reply => reply.assignments ?? []));
    }

    retrieveTrabajadorNet4WorkSummary(request: RetrieveTrabajadorNet4WorkSummaryRequest) {
        return this.httpService.post<RetrieveWorkSummaryReply>(Net2BeepleMethodsEnum.retrieveTrabajadorNet4WorkSummary, request)
        .pipe(map(reply => this.fillNoWorkedDays(request, reply)));
    }

    retrieveNet4WorkSummary(request: RetrieveNet4WorkSummaryRequest) {
        return this.httpService.post<RetrieveWorkSummaryReply>(Net2BeepleMethodsEnum.retrieveNet4WorkSummary, request)
        .pipe(map(reply => this.fillNoWorkedDays(request, reply)));
    }

    hackContractMinutes(request: HackContractMinutesRequest) {
        return this.httpService.post<HackContractMinutesReply>(Net2BeepleMethodsEnum.hackContractMinutes, request);
    }
    hackContractMinutesV2(request: HackContractMinutesV2Request) {
        return this.httpService.post<HackContractMinutesReply>(Net2BeepleMethodsEnum.hackContractMinutesV2, request);
    }

    downloadWorkplaceWorkSummaryXSSF(body: DownloadWorkplaceWorkSummaryXSSFRequest): any {
        return this.httpService.postWithHeaders<any>(Net2BeepleMethodsEnum.downloadWorkplaceWorkSummaryXSSF, body, 'blob');
    }

    downloadContractWorkSummaryXSSF(body: DownloadContractWorkSummaryXSSFRequest): any {
        return this.httpService.postWithHeaders<any>(Net2BeepleMethodsEnum.downloadContractWorkSummaryXSSF, body, 'blob');
    }

    downloadTrabajadorNet4WorkSummaryXSSF(body: DownloadTrabajadorNet4WorkSummaryXSSFRequest): any {
        return this.httpService.postWithHeaders<any>(Net2BeepleMethodsEnum.downloadTrabajadorNet4WorkSummaryXSSF, body, 'blob');
    }

    downloadNet4WorkSummaryXSSF(body: DownloadNet4WorkSummaryXSSFRequest): any {
        return this.httpService.postWithHeaders<any>(Net2BeepleMethodsEnum.downloadNet4WorkSummaryXSSF, body, 'blob');
    }

    searchEmpresaBusPartaker() {
        const request: SearchBusPartakerRequest = { conceptToSearch: 'empresa', options: true };
        return this.httpService.post<{ option: SelectOption[] }>(Net2BeepleMethodsEnum.searchBusPartaker, request);
    }

    retrieveMonthlyContractWorkConceptSummary(request: RetrieveMonthlyContractWorkConceptSummaryRequest) {
        return this.httpService.post<RetrieveMonthlyContractWorkConceptSummaryReply>(Net2BeepleMethodsEnum.retrieveMonthlyContractWorkConceptSummary, request)
            .pipe(map(reply => {
                const rows: MonthlyContractWorkConceptSummary[] = reply.rows.map(row => {
                    const cols = new Map<number, ConceptSummaryColumn>();
                    for (const col of Object.entries(row.cols)) {
                        cols.set(col[1].net4ConceptId, { ...col[1], oid: col[0] });
                    }
                    return { ...row, cols };
                });
                const transformedReply: RetrieveMonthlyContractWorkConceptSummaryTransformedReply = { ...reply, rows };
                return transformedReply;
            }));
    }

    validateContractWorkplaceWorkConceptSummary(request: ValidateMonhtlyContractWorkConceptSummaryRequest) {
        return this.httpService.post<ValidateContractWorkConceptSummaryReply>(Net2BeepleMethodsEnum.validateMonthlyContractWorkConceptSummary, request);
    }

    flushMonthlyContractWorkConceptSummary(request: FlushMonthlyContractWorkConceptSummaryRequest) {
        return this.httpService.post<FlushMonthlyContractWorkConceptSummaryReply>(Net2BeepleMethodsEnum.flushMonthlyContractWorkConceptSummary, request);
    }

    getRoleByParams(params: Params): UserNodePrivileges {
        if (params.company && this.endpoint()?.role) {
            const company = this.endpoint();
            return { ...company.role };
        } else if (params.service) {
            const service = this._nextService();
            return { ...service?.role };
        } else if (params.client) {
            const client = this._beepleClient();
            return { ...client?.role };
        } else if (params.http_endpoint) {
            const httpEndpoint = this._httpEndpoint();
            return { ...httpEndpoint?.role };
        } else if (params.ts_endpoint) {
            const endpoint = this._endpoint();
            return { ...endpoint?.role };
        } else {
            const role = this.ouService.role();
            return role;
        }
    }

    buildLocatorFromParams(params: Params): [Net2BeepleLocator, string] {
        const locator: Net2BeepleLocator = {
            OU: '01',
            TS: params.ts_endpoint,
            HS: params.http_endpoint,
            CL: params.client,
            SV: params.service,
        };

        let stringLocator = '';
        if (params.company) {
            stringLocator += `EM=${params.company},`;
        }
        if (params.service) {
            stringLocator += `SV=${params.service},`;
        }
        if (params.client) {
            stringLocator += `CL=${params.client},`;
        }
        if (params.http_endpoint) {
            stringLocator += `HS=${params.http_endpoint},`;
        }
        if (params?.ts_endpoint) {
            stringLocator += `TS=${params.ts_endpoint},`;
        }
        stringLocator += `OU=01`;

        return [locator, stringLocator];
    }

    getBelongsToCaption(locator: string, nextlocator: Net2BeepleLocator, partaker: Partaker) {
        const split = locator.split(',');
        const firstLocatorSplit = split[0].split('=');
        let uri: string;
        switch (firstLocatorSplit[0]) {
            case 'OU':
                return partaker[OU_URI]?.[firstLocatorSplit[1]]?.name;
            case 'TS':
                return `${partaker[MasterEndpointTargetSystemUri]?.[firstLocatorSplit[1]]?.name} (${this.transloco.translate('endpointTargetSystem')})`;
            case 'HS':
                return `${partaker[HttpServiceEndpointUri]?.[firstLocatorSplit[1]]?.name} (${this.transloco.translate('httpEndpoints')})`;
            case 'CL':
                uri = `master://HS=${nextlocator.HS}/TS=${nextlocator.TS}/OU=01/BeepleClient`;
                return `${partaker[uri]?.[firstLocatorSplit[1]]?.name} (${this.transloco.translate('beepleClients')})`;
            case 'SV':
                uri = `master://HS=${nextlocator.HS}/TS=${nextlocator.TS}/OU=01/NextServicio_n2b`;
                return `${partaker[uri]?.[firstLocatorSplit[1]]?.name} (${this.transloco.translate('nextServices')})`;
            case 'EM':
                if (nextlocator.CL) {
                    uri = `master://CL=${nextlocator.CL}/HS=${nextlocator.HS}/TS=${nextlocator.TS}/OU=01/Net2BeepleEmpresa`;
                } else if (nextlocator.SV) {
                    uri = `master://SV=${nextlocator.SV}/HS=${nextlocator.HS}/TS=${nextlocator.TS}/OU=01/Net2BeepleEmpresa`;
                }
                return uri ? `${partaker[uri]?.[firstLocatorSplit[1]]?.name} (${this.transloco.translate('net2BeepleCompanies')})` : '';
            default:
                return '';
        }
    }

    isTheSameLocator(locator: string, objectLocator: string) {
        const split = locator.split(',');
        const dtypeSplit = objectLocator.split(',');
        return dtypeSplit[0] === split[0];
    }

    syncClients(beepleEndpoint: string) {
        return this.httpService.post<JsonReply>(Net2BeepleMethodsEnum.synchronizeBeepleClient, { beepleEndpoint });
    }

    syncServices(endpoint: string) {
        return this.httpService.post<SynchronizeNextServicio_n2bReply>(Net2BeepleMethodsEnum.synchronizeNextServicio_n2b, { endpoint })
            .pipe(map(reply => {
                const services: NextServicio[] = reply.retrieveDTypeDataCollectionReply.data ?? [];
                return services.map(service => {
                    const payload = service.nodeInfo ? JSON.parse(service.nodeInfo) : undefined;
                    if (payload) {
                        service.brand = {
                            code: payload.code,
                            name: payload.name,
                            logoURL: payload.logoURL
                        };
                        if (payload.node) {
                            service.client = {
                                code: payload.node.code,
                                name: payload.node.name,
                                logoURL: payload.node.logoURL
                            };
                            if (payload.node.node) {
                                service.project = {
                                    code: payload.node.node.code,
                                    name: payload.node.node.name,
                                    logoURL: payload.node.node.logoURL
                                };
                            }
                        }
                    }
                    return service;
                });
            }));
    }

    toggleHourBag (request: ToggleHourBagRequest) {
        return this.httpService.post<ToggleHourBagReply>(Net2BeepleMethodsEnum.toggleHourBag, request);
    }

    regularizeHourBag(request: RegularizeHourBagRequest) {
        return this.httpService.post<RegularizeHourBagReply | RegularizeHourBagWithConceptsReply>(Net2BeepleMethodsEnum.regularizeHourBag, request)
    }

    changeWorkplaceGuide (request: ChangeWorkplaceGuideRequest) {
        return this.httpService.post<ChangeWorkplaceGuideReply>(Net2BeepleMethodsEnum.changeWorkplaceGuide, request).pipe(
           catchError((error) => {
                this.dialogsService.openErrorDialog({
                    message: Object.keys(error?.error?.echo).length > 0 ? this.localize.transform(error.error.echo) : undefined,
                });
                return throwError(() => error);
            })
        );
    }

    retrieveNet4WorkerGroup (request: RetrieveNet4WorkerGroupRequest) {
        return this.httpService.post<RetrieveNet4WorkerGroupReply>(Net2BeepleMethodsEnum.retrieveNet4WorkerGroup, request);
    }

    flushNet4WorkerGroup (request: FlushNet4WorkerGroupRequest) {
        return this.httpService.post<FlushNet4WorkerGroupReply>(Net2BeepleMethodsEnum.flushNet4WorkerGroup, request);
    }

    private fillNoWorkedDays(request: RetrieveWorkplaceWorkSummaryRequest | RetrieveTrabajadorNet4WorkSummaryRequest | RetrieveNet4WorkSummaryRequest, reply: RetrieveWorkSummaryReply ) {
        reply.workableDates = reply.workableDates ?? [];
        reply.holidayDates = reply.holidayDates ?? [];
        reply.rows = reply.rows ?? [];
        const date = setYear(setMonth(new Date, Number(request.period) - 1), Number(request.year));
        const indexesToDelete: number[] = [];
        reply.rows.forEach((row, index) => {
            if (row.dayCell === undefined) {
                indexesToDelete.push(index);
            }
            eachDayOfInterval({ start: startOfMonth(date), end: endOfMonth(date) }).forEach((day, index) => {
                const dateString = format(day, 'yyyy-MM-dd');
                if (row.dayCell?.findIndex(d => d.date === dateString) === -1) {
                    const workable = reply.workableDates.findIndex(d => d === dateString) > -1;
                    const holiday = reply.holidayDates.findIndex(d => d === dateString) > -1;
                    row.dayCell.splice(index, 0, {
                        absenceMinutes: {},
                        workedMinutes: -1,
                        date: dateString,
                        holiday,
                        workable,
                    });
                }
            });
        });
        indexesToDelete.reverse().forEach(index => {
            reply.rows.splice(index, 1);
        });
        return reply;
}


//TODO: usar un sólo método para rellenar los días no trabajados, tanto en el resumen de trabajador como en el de contrato
private fillNoWorkedDaysContract(request: RetrieveContractWorkSummaryRequest, reply: RetrieveContractWorkSummaryReply) {
    reply.workableDates = reply.workableDates ?? [];
    reply.holidayDates = reply.holidayDates ?? [];
    reply.rows = reply.rows ?? [];
    const date = setYear(setMonth(new Date(), Number(request.period) - 1), Number(request.year));
    const indexesToDelete: number[] = [];
    reply.rows.forEach((row, index) => {
        if (row.dayCell === undefined) {
            indexesToDelete.push(index);
        }
        eachDayOfInterval({ start: startOfMonth(date), end: endOfMonth(date) }).forEach((day, idx) => {
            const dateString = format(day, 'yyyy-MM-dd');
            if (row.dayCell?.findIndex(d => d.date === dateString) === -1) {
                const workable = reply.workableDates.findIndex(d => d === dateString) > -1;
                const holiday = reply.holidayDates.findIndex(d => d === dateString) > -1;
                row.dayCell.splice(idx, 0, {
                    absenceMinutes: {},
                    workedMinutes: -1,
                    date: dateString,
                    holiday,
                    workable,
                });
            }
        });
        //TODO: usar el partaker para tener más info del workplaceData
        // if (Array.isArray(row.workplaceData) && reply.partaker) {
        //     const uri = `master://HS=${row.originatorEndpointId}/TS=${row.endpointTargetSystem}/OU=01/NextServicio_n2b`;
        //     row.workplaceData.forEach(wd => {
        //         // Si partaker es un objeto plano con los oid como claves:
        //         const workplaceInfo = reply.partaker[wd.originatorEndpointId];
        //         if (workplaceInfo) {
        //             // Ejemplo: añade el nombre y cualquier otro campo necesario
        //             wd.originatorEndpointName = workplaceInfo.name;

        //         }
        //     });
        // }
    });

    indexesToDelete.reverse().forEach(index => {
        reply.rows.splice(index, 1);
    });
    return reply;
    }
}
